import React, { useEffect, useState } from 'react';
import AutoCarouselButton from './AutoCarouselButton';
import GoalRegenerateChip from './GoalRegenerateChip';
import {
  Sparkles,
  Link2,
  Newspaper,
  ExternalLink,
  Loader2,
  Copy,
  Check,
  RefreshCcw,
  Quote,
  Flame,
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../ui/tabs';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Skeleton } from '../ui/skeleton';
import { Switch } from '../ui/switch';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { ToggleGroup, ToggleGroupItem } from '../ui/toggle-group';
import { POST_COUNTRY_OPTIONS, resolveDefaultCountry } from '../../lib/countries';
import { toast } from 'sonner';
import { linkedge, api } from '../../lib/api';

function formatDate(d) {
  if (!d) return '';
  try {
    const dt = new Date(d);
    if (Number.isNaN(dt.getTime())) return '';
    return dt.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  } catch (_e) {
    return '';
  }
}

function renderInlineCitations(text, citations) {
  if (!text) return null;
  // Replace [^n] tokens with clickable superscript links
  const tokens = text.split(/(\[\^\d+\])/g);
  return tokens.map((tok, idx) => {
    const m = /\[\^(\d+)\]/.exec(tok);
    if (m) {
      const n = parseInt(m[1], 10);
      const c = citations?.find((x) => x.n === n);
      if (c?.url) {
        return (
          <a
            key={idx}
            href={c.url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary hover:underline align-super text-[10px] font-mono-edge mx-0.5"
            title={`${c.publication || ''} — ${c.title || c.url}`}
            data-testid={`pov-inline-cite-${n}`}
          >
            [{n}]
          </a>
        );
      }
      return (
        <sup key={idx} className="text-[10px] font-mono-edge text-muted-foreground mx-0.5">
          [{n}]
        </sup>
      );
    }
    return <React.Fragment key={idx}>{tok}</React.Fragment>;
  });
}

function NewsItemCard({ item, selected, onSelect, idx }) {
  return (
    <button
      type="button"
      onClick={() => onSelect(item)}
      className={`text-left w-full transition-colors ${
        selected ? 'ring-2 ring-primary' : ''
      }`}
      data-testid={`pov-news-item-${idx}`}
    >
      <Card className={`border-border ${selected ? 'border-primary' : ''}`}>
        <CardContent className="p-4">
          <div className="flex items-start justify-between gap-2 mb-1.5">
            <Badge className="font-mono-edge text-[10px] bg-secondary text-foreground border-border">
              {item.source_publication || 'Source'}
            </Badge>
            <span className="text-[10px] font-mono-edge text-muted-foreground">
              {formatDate(item.published_at)}
            </span>
          </div>
          <p className="text-sm font-medium leading-snug line-clamp-2">{item.title}</p>
          {item.summary && (
            <p className="text-xs text-muted-foreground mt-1.5 line-clamp-2">{item.summary}</p>
          )}
        </CardContent>
      </Card>
    </button>
  );
}

export default function PovDialog({ open, onClose, analysisId, onPovGenerated, defaultCountry, defaultLengthMode, seedTheme }) {
  const [tab, setTab] = useState('news');
  const [news, setNews] = useState(null);
  const [newsLoading, setNewsLoading] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [url, setUrl] = useState('');
  const [generating, setGenerating] = useState(false);
  const [pov, setPov] = useState(null);
  const [copied, setCopied] = useState(false);
  // "Connect to my functional expertise" toggle - same semantics as the
  // Prompt Posts tab. ON (default) anchors the POV to the user's seat;
  // OFF lets the POV stand on its own without forced anchoring back to
  // the author's day-job function / industry.
  const [connectToExpertise, setConnectToExpertise] = useState(true);
  // Manual country / region anchor for THIS POV. Defaults to whatever the
  // analysis post_context.geography currently is (auto-inferred unless the
  // user has previously overridden it). When the user picks a value other
  // than "Global", the backend persists it as the new sticky default.
  const [country, setCountry] = useState(resolveDefaultCountry(defaultCountry));
  // Length mode: short (~120-200 words, compressed) or long (~220-400 words,
  // full thought-leadership structure). POVs default to LONG because the
  // structure benefits from the room to breathe.
  const [lengthMode, setLengthMode] = useState(defaultLengthMode === 'short' ? 'short' : 'long');
  // Goal-first framework — mirrors PromptPostsTab. Optional; when set,
  // the POV writer follows the goal-specific framework instead of the
  // generic POV shape.
  const [goal, setGoal] = useState('');
  const [goalCatalog, setGoalCatalog] = useState([]);
  useEffect(() => {
    let alive = true;
    api.get('/posts/goals').then((r) => { if (alive) setGoalCatalog(r?.data?.goals || []); }).catch(() => {});
    return () => { alive = false; };
  }, []);
  const activeGoal = goalCatalog.find((g) => g.key === goal) || null;

  useEffect(() => {
    if (open) {
      setCountry(resolveDefaultCountry(defaultCountry));
      setLengthMode(defaultLengthMode === 'short' ? 'short' : 'long');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, defaultCountry, defaultLengthMode]);

  useEffect(() => {
    if (open && !news && !newsLoading) {
      loadNews();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const loadNews = async () => {
    setNewsLoading(true);
    try {
      const data = await linkedge.povNewsList(analysisId);
      setNews(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not fetch trending news');
    } finally {
      setNewsLoading(false);
    }
  };

  const handleGenerate = async () => {
    const payload = {
      connect_to_expertise: connectToExpertise,
      country_override: country,
      length_mode: lengthMode,
      goal: goal || undefined,
    };
    if (tab === 'news' && selectedItem) {
      payload.article_url = selectedItem.source_url;
      payload.headline = selectedItem.title;
    } else if (tab === 'url' && url.trim()) {
      payload.article_url = url.trim();
    } else {
      toast.error('Pick a news story or paste a URL');
      return;
    }
    setGenerating(true);
    setPov(null);
    try {
      const data = await linkedge.povGenerate(analysisId, payload);
      setPov(data);
      onPovGenerated?.(data);
      toast.success('PoV post generated');
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || 'PoV generation failed');
    } finally {
      setGenerating(false);
    }
  };

  const handleCopy = async () => {
    if (!pov?.post_text) return;
    // Copy with citation markers; user can paste & edit
    try {
      await navigator.clipboard.writeText(pov.post_text);
      setCopied(true);
      toast.success('PoV post copied');
      setTimeout(() => setCopied(false), 1500);
    } catch (_e) {
      toast.error('Could not copy');
    }
  };

  const reset = () => {
    setPov(null);
    setSelectedItem(null);
    setUrl('');
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) {
          onClose?.();
        }
      }}
    >
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="pov-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles size={16} className="text-primary" />
            Generate a Point-of-View post
          </DialogTitle>
          <DialogDescription>
            Pick a trending story or paste any article URL. We will write a sharp, voice-matched LinkedIn post
            with inline citations to verifiable sources.
          </DialogDescription>
        </DialogHeader>

        {/* Seed theme banner — visible when a user opened this dialog by
            clicking "Draft a POV from this theme" on the Presence-Plan
            tab. Reminds them to pick a story that supports the theme. */}
        {seedTheme && !pov && (
          <div
            className="rounded-lg border border-primary/30 bg-primary/[0.06] px-3 py-2.5 text-[13px] flex items-start gap-2"
            data-testid="pov-seed-theme-banner"
          >
            <Sparkles size={14} className="text-primary shrink-0 mt-0.5" />
            <div className="min-w-0">
              <p className="text-[10px] font-mono-edge tracking-[0.18em] uppercase text-primary/80">Framing this POV around</p>
              <p className="font-medium text-foreground">{seedTheme.theme}</p>
              {seedTheme.why && <p className="text-[12px] text-muted-foreground mt-0.5">{seedTheme.why}</p>}
              <p className="text-[11px] text-muted-foreground mt-1">Pick a story below that touches this theme — we&rsquo;ll wire the citation.</p>
            </div>
          </div>
        )}

        {!pov && (
          <Tabs value={tab} onValueChange={setTab} className="w-full">
            <TabsList className="bg-secondary rounded-lg p-1" data-testid="pov-source-tabs">
              <TabsTrigger value="news" className="rounded-md" data-testid="pov-tab-news">
                <Newspaper size={14} className="mr-1.5" /> Pick from trending
              </TabsTrigger>
              <TabsTrigger value="url" className="rounded-md" data-testid="pov-tab-url">
                <Link2 size={14} className="mr-1.5" /> Paste URL
              </TabsTrigger>
            </TabsList>

            {/* Trending news picker */}
            <TabsContent value="news" className="mt-4">
              <div className="flex items-center justify-between mb-3">
                <p className="text-sm text-muted-foreground">
                  Trending in your role + industry. Sourced from real, cited news.
                </p>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={loadNews}
                  disabled={newsLoading}
                  data-testid="pov-refresh-news"
                >
                  {newsLoading ? (
                    <Loader2 size={12} className="mr-1.5 animate-spin" />
                  ) : (
                    <RefreshCcw size={12} className="mr-1.5" />
                  )}
                  Refresh
                </Button>
              </div>
              {newsLoading ? (
                <div className="grid sm:grid-cols-2 gap-3">
                  {Array.from({ length: 6 }).map((_, i) => (
                    <Skeleton key={i} className="h-24 rounded-lg" />
                  ))}
                </div>
              ) : news?.items?.length ? (
                <div className="grid sm:grid-cols-2 gap-3 max-h-[400px] overflow-y-auto pr-1">
                  {news.items.map((it, i) => (
                    <NewsItemCard
                      key={it.id || i}
                      item={it}
                      idx={i}
                      selected={selectedItem?.source_url === it.source_url}
                      onSelect={setSelectedItem}
                    />
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground text-center py-8">
                  No trending stories returned. Try again in a moment.
                </p>
              )}
            </TabsContent>

            {/* URL paste */}
            <TabsContent value="url" className="mt-4 space-y-3">
              <p className="text-sm text-muted-foreground">
                Paste any article, blog, or Substack URL. We extract key facts, then synthesize a PoV with
                inline citations to the original source.
              </p>
              <Input
                placeholder="https://www.example.com/article"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                data-testid="pov-url-input"
              />
            </TabsContent>
          </Tabs>
        )}

        {/* Generating state */}
        {generating && (
          <div className="py-10 text-center" data-testid="pov-generating">
            <Loader2 size={28} className="animate-spin text-primary mx-auto" />
            <p className="text-sm text-muted-foreground mt-3">
              Researching the source and writing your point of view…
            </p>
            <p className="text-[11px] font-mono-edge text-muted-foreground mt-1">
              Typically 20–40 seconds.
            </p>
          </div>
        )}

        {/* Result */}
        {pov && !generating && (
          <div className="space-y-4" data-testid="pov-result">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="flex items-center gap-2">
                <Flame size={14} className="text-[hsl(var(--warning))]" />
                <p className="text-xs font-mono-edge text-primary tracking-[0.16em]">YOUR POV POST</p>
                {pov.topic && (
                  <Badge className="font-mono-edge text-[10px]">{pov.topic}</Badge>
                )}
              </div>
              <div className="flex items-center gap-2">
                {analysisId && (
                  <GoalRegenerateChip
                    analysisId={analysisId}
                    source="pov"
                    currentGoal={pov.goal || null}
                    compact
                    onReplaced={(np) => {
                      // POV returns the updated pov shape (`np` may
                      // already carry post_text + goal). Merge and
                      // notify parent so the panel restates.
                      setPov((prev) => ({ ...(prev || {}), ...np, framework_regenerated: true }));
                    }}
                  />
                )}
                <Button variant="ghost" size="sm" onClick={handleCopy} data-testid="pov-copy-button">
                  {copied ? (
                    <Check size={12} className="mr-1.5 text-[hsl(var(--success))]" />
                  ) : (
                    <Copy size={12} className="mr-1.5" />
                  )}
                  {copied ? 'Copied' : 'Copy post'}
                </Button>
                <AutoCarouselButton
                  analysisId={analysisId}
                  sourceType="pov"
                  sourceIdx={0}
                  variant="outline"
                  size="sm"
                  postPreview={pov?.post_text || pov?.content || pov?.text || ''}
                />
              </div>
            </div>

            {pov.stance && (
              <div className="rounded-lg border border-primary/20 bg-primary/5 p-3 flex gap-2">
                <Quote size={14} className="text-primary mt-0.5 shrink-0" />
                <div>
                  <p className="text-[10px] font-mono-edge text-primary tracking-[0.14em] mb-0.5">STANCE</p>
                  <p className="text-sm">{pov.stance}</p>
                </div>
              </div>
            )}

            <Card className="border-border" data-testid="pov-post-card">
              <CardContent className="p-5">
                <p className="text-sm whitespace-pre-wrap leading-relaxed" data-testid="pov-post-text">
                  {renderInlineCitations(pov.post_text, pov.citations || [])}
                </p>
              </CardContent>
            </Card>

            {pov.citations?.length > 0 && (
              <div data-testid="pov-citations">
                <p className="text-[10px] font-mono-edge tracking-[0.16em] text-muted-foreground mb-2">
                  SOURCES ({pov.citations.length})
                </p>
                <ul className="space-y-2">
                  {pov.citations.map((c) => (
                    <li
                      key={c.n}
                      className="flex items-start gap-2 text-xs"
                      data-testid={`pov-citation-${c.n}`}
                    >
                      <span className="font-mono-edge text-primary shrink-0 mt-0.5">[{c.n}]</span>
                      <a
                        href={c.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="hover:text-primary hover:underline break-all"
                      >
                        {c.title || c.url}
                        <ExternalLink size={10} className="inline ml-1 opacity-60" />
                      </a>
                      <span className="text-muted-foreground shrink-0">
                        {c.publication ? `· ${c.publication}` : ''}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        {/* "Connect to my functional expertise" toggle - hidden once a
            POV has been generated since the choice is locked in */}
        {!pov && (
          <>
            {/* Goal-first framework picker — mirrors PromptPostsTab. */}
            <div
              className="rounded-lg border border-border bg-secondary/40 px-3.5 py-3 mt-2 space-y-2"
              data-testid="pov-goal-row"
            >
              <div className="flex items-center justify-between gap-2">
                <Label htmlFor="pov-goal-select" className="text-sm font-medium">Goal &amp; framework</Label>
                {activeGoal && (
                  <Badge variant="secondary" className="text-[10.5px] font-mono-edge tracking-[0.12em]">
                    {activeGoal.framework_name}
                  </Badge>
                )}
              </div>
              <p className="text-[11px] text-muted-foreground leading-relaxed">
                Pick a goal and the POV follows that framework&apos;s shape. Optional.
              </p>
              <Select
                value={goal || '__auto__'}
                onValueChange={(v) => setGoal(v === '__auto__' ? '' : v)}
              >
                <SelectTrigger id="pov-goal-select" className="h-9 text-xs" data-testid="pov-goal-select">
                  <SelectValue placeholder="Auto — use the classic POV shape" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__auto__">Auto — classic POV shape</SelectItem>
                  {goalCatalog.map((g) => (
                    <SelectItem key={g.key} value={g.key} data-testid={`pov-goal-option-${g.key}`}>
                      {g.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div
              className="flex items-start justify-between gap-4 rounded-lg border border-border bg-secondary/40 px-3.5 py-3 mt-2"
              data-testid="pov-length-row"
            >
              <div className="min-w-0 flex-1">
                <Label className="text-sm font-medium">Post length</Label>
                <p className="text-[11px] text-muted-foreground mt-1 leading-relaxed">
                  Short = scene + hidden problem + memorable close. Long = full thought-leadership structure with research support.
                </p>
              </div>
              <ToggleGroup
                type="single"
                value={lengthMode}
                onValueChange={(v) => v && setLengthMode(v)}
                className="grid grid-cols-2 gap-1 w-44 shrink-0"
                data-testid="pov-length-toggle"
              >
                <ToggleGroupItem
                  value="short"
                  className="data-[state=on]:bg-primary data-[state=on]:text-primary-foreground rounded-md border border-border h-9 text-xs"
                  data-testid="pov-length-short"
                >
                  Short
                </ToggleGroupItem>
                <ToggleGroupItem
                  value="long"
                  className="data-[state=on]:bg-primary data-[state=on]:text-primary-foreground rounded-md border border-border h-9 text-xs"
                  data-testid="pov-length-long"
                >
                  Long
                </ToggleGroupItem>
              </ToggleGroup>
            </div>

            <div
              className="flex items-start justify-between gap-4 rounded-lg border border-border bg-secondary/40 px-3.5 py-3 mt-2"
              data-testid="pov-country-row"
            >
              <div className="min-w-0 flex-1">
                <Label
                  htmlFor="pov-country-select"
                  className="text-sm font-medium cursor-pointer"
                >
                  Country / region context
                </Label>
                <p className="text-[11px] text-muted-foreground mt-1 leading-relaxed">
                  Anchors examples, currency, spelling, and regulators to this market. Saved as the default for this analysis.
                </p>
              </div>
              <div className="w-44 shrink-0">
                <Select value={country} onValueChange={setCountry}>
                  <SelectTrigger id="pov-country-select" data-testid="pov-country-select">
                    <SelectValue placeholder="Pick a country" />
                  </SelectTrigger>
                  <SelectContent>
                    {POST_COUNTRY_OPTIONS.map((c) => (
                      <SelectItem
                        key={c.value}
                        value={c.value}
                        data-testid={`pov-country-option-${c.value.toLowerCase().replace(/\s+/g, '-')}`}
                      >
                        {c.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div
              className="flex items-start justify-between gap-4 rounded-lg border border-border bg-secondary/40 px-3.5 py-3 mt-2"
              data-testid="pov-connect-expertise-row"
            >
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <Link2 size={13} className="text-primary shrink-0" />
                <Label
                  htmlFor="pov-connect-expertise"
                  className="text-sm font-medium cursor-pointer"
                >
                  Connect to my functional expertise
                </Label>
              </div>
              <p className="text-[11px] text-muted-foreground mt-1 leading-relaxed">
                {connectToExpertise
                  ? 'The POV will be written from your seat (role, industry) - good when the topic is in your professional lane.'
                  : 'The POV will treat the story on its own. The writer will NOT shoehorn it back to your day-job category - good for reactions to news outside your lane.'}
              </p>
            </div>
            <Switch
              id="pov-connect-expertise"
              checked={connectToExpertise}
              onCheckedChange={setConnectToExpertise}
              data-testid="pov-connect-expertise-toggle"
            />
          </div>
          </>
        )}

        <DialogFooter className="flex-row gap-2 justify-end">
          {pov ? (
            <>
              <Button variant="secondary" onClick={reset} data-testid="pov-generate-another">
                <Sparkles size={14} className="mr-1.5" /> Generate another
              </Button>
              <Button onClick={onClose}>Done</Button>
            </>
          ) : (
            <>
              <Button variant="ghost" onClick={onClose} disabled={generating}>
                Cancel
              </Button>
              <Button
                onClick={handleGenerate}
                disabled={generating || (tab === 'news' ? !selectedItem : !url.trim())}
                data-testid="pov-generate-cta"
              >
                {generating ? (
                  <Loader2 size={14} className="mr-1.5 animate-spin" />
                ) : (
                  <Sparkles size={14} className="mr-1.5" />
                )}
                {generating ? 'Writing…' : 'Generate PoV post'}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
